import type React from 'react';
import type { CSSProperties } from 'react';
import { useCallback, useEffect, useRef } from 'react';
import omit from 'lodash/fp/omit';
import mapValues from 'lodash/mapValues';
import isString from 'lodash/isString';
import isEqual from 'lodash/isEqual';
import { useIntl } from 'react-intl';
import { ClassNames, css, useTheme } from '@emotion/react';
import useStores from 'hooks/use-stores.hooks';
import { observer } from 'mobx-react';
import type { NotificationArgs } from 'types/notifications.types';
import { notification as antdNotification } from 'antd';
import { isTranslationId } from 'components/localization';

// Switch-off keeps passing this exact object, so its DOM is unchanged by construction.
const LEGACY_NOTICE_STYLE: CSSProperties = { whiteSpace: 'pre-line', width: 'fit-content', maxWidth: 500 };

/**
 * `className` lands on `.ant-notification-notice`, where antd declares `width` and `max-width` - so
 * those two need doubling. `white-space` it never declares.
 */
const newDesignNotice = css`
  white-space: pre-line;

  && {
    width: fit-content;
    max-width: 500px;
  }
`;

type EmitterProps = { noticeClassName?: string };

const Emitter: React.FunctionComponent<EmitterProps> = ({ noticeClassName }) => {
  const { notificationStore } = useStores();
  const { notification } = notificationStore;
  const [notificationsApi, contextHolder] = antdNotification.useNotification();

  const lastNotification = useRef<NotificationArgs | null>(null);

  const intl = useIntl();

  const onNotificationClose = useCallback(() => (lastNotification.current = null), []);

  useEffect(() => {
    if (notification) {
      if (!notification.message) {
        return;
      }

      // a variable can itself be a translation key (e.g. the resource name of a failed API call)
      const messageVariables =
        notification.messageVariables &&
        mapValues(notification.messageVariables, value =>
          isString(value) && isTranslationId(value) ? intl.formatMessage({ id: value }) : value
        );

      // api failures arrive here as the server's own English text, not as a translation key
      const message = isTranslationId(notification.message)
        ? intl.formatMessage({ id: notification.message }, messageVariables)
        : notification.message;

      // mapped API failures share one message key ('api.error.failedToLoad') and differ only in
      // messageVariables, so the variables are part of the toast's identity
      if (
        lastNotification.current?.type !== notification?.type ||
        lastNotification.current?.message !== notification?.message ||
        !isEqual(lastNotification.current?.messageVariables, notification?.messageVariables)
      ) {
        // only way to get an html identifier on the notification
        const e2eClassName = `e2e-${notification.notificationType}-notification`;

        notificationsApi[notification.notificationType]({
          ...omit(['notificationType', 'messageVariables'], notification),
          message,
          style: noticeClassName ? undefined : LEGACY_NOTICE_STYLE,
          className: noticeClassName ? `${e2eClassName} ${noticeClassName}` : e2eClassName,
          onClose: onNotificationClose,
          duration: notification.notificationType === 'error' ? 0 : 5
        });

        lastNotification.current = notification;
      }
    }
  }, [notificationsApi, intl, notification, onNotificationClose, noticeClassName]);

  return contextHolder;
};

const NotificationEmitter = observer(Emitter);

/**
 * The class has to be a string before the effect can hand it to antd's imperative API, and
 * `ClassNames` is a render prop - hence the split from the emitter above. `toClassName` is called only
 * on the new-design path, so switch-off injects no stylesheet at all.
 */
const NotificationManager: React.FunctionComponent = () => {
  const { isNewDesign } = useTheme();

  return (
    <ClassNames>
      {({ css: toClassName }) => (
        <NotificationEmitter noticeClassName={isNewDesign ? toClassName(newDesignNotice) : undefined} />
      )}
    </ClassNames>
  );
};

export default observer(NotificationManager);
